function getToken() {
  try {
    const s = localStorage.getItem('sms_auth');
    return s ? JSON.parse(s)?.token : null;
  } catch { return null; }
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

let _loggingOut = false;

async function req(method, path, body) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new Error(`Server error (HTTP ${res.status})`);
  }
  const data = await res.json();
  if (!res.ok) {
    // Token expired or invalid — clear session and go to login
    if (res.status === 401 && getToken() && !_loggingOut) {
      _loggingOut = true;
      localStorage.removeItem('sms_auth');
      window.location.href = '/';
    }
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export const api = {
  // Auth
  login: (email, password) => req('POST', '/auth/login', { email, password }),

  // Generic CRUD
  list:   (entity)          => req('GET',    `/${entity}`),
  create: (entity, data)    => req('POST',   `/${entity}`, data),
  update: (entity, id, data) => req('PUT',  `/${entity}/${id}`, data),
  remove: (entity, id)      => req('DELETE', `/${entity}/${encodeURIComponent(id)}`),

  // Users (admin)
  listUsers:  ()            => req('GET',    '/users'),
  createUser: (data)        => req('POST',   '/users', data),
  updateUser: (id, data)    => req('PUT',    `/users/${id}`, data),
  deleteUser: (id)          => req('DELETE', `/users/${id}`),
  changePassword: (id, currentPassword, newPassword) =>
    req('PUT', `/users/${id}/password`, { currentPassword, newPassword }),

  // Dashboard
  dashboard: () => req('GET', '/dashboard'),

  // Question Papers — FA Schedule
  getFASchedule: () => req('GET', '/fa-schedule'),
  saveFASchedule: (data) => req('POST', '/fa-schedule', data),

  // Student bulk import
  importStudents: (students) => req('POST', '/students/import', { students }),

  // Teacher mapping
  getTeacherMapping: () => req('GET', '/teacher-mapping/me'),

  // School settings
  getSchoolSettings:    ()       => req('GET',  '/school-settings'),
  saveSchoolSettings:   (data)   => req('POST', '/school-settings', data),

  // Attendance policy
  getAttendancePolicy:  ()       => req('GET',  '/attendance-policy'),
  saveAttendancePolicy: (data)   => req('POST', '/attendance-policy', data),

  // WhatsApp
  getWhatsAppSettings:  ()       => req('GET',  '/whatsapp/settings'),
  saveWhatsAppSettings: (data)   => req('POST', '/whatsapp/settings', data),
  testWhatsApp:         (phone)  => req('POST', '/whatsapp/test', { phone }),

  // Generic HTTP helpers (for ad-hoc endpoints)
  get:  (path)        => req('GET',    path.replace(/^\/api/, '')),
  post: (path, body)  => req('POST',   path.replace(/^\/api/, ''), body),
  put:  (path, body)  => req('PUT',    path.replace(/^\/api/, ''), body),

  // Leave management
  getLeaveTypes:    ()           => req('GET',  '/leave-types'),
  getLeaveRequests: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/leave-requests${qs ? `?${qs}` : ''}`);
  },
  approveLeave: (id, remarks)   => req('PUT',  `/leave-requests/${id}/approve`, { remarks }),
  rejectLeave:  (id, remarks)   => req('PUT',  `/leave-requests/${id}/reject`,  { remarks }),

  // Salary
  getSalaryStructures:  ()       => req('GET',  '/salary-structures'),
  saveSalaryStructure:  (data)   => req('POST', '/salary-structures', data),
  getSalarySlips: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/salary-slips${qs ? `?${qs}` : ''}`);
  },
  generateSalarySlips:  (data)   => req('POST', '/salary-slips/generate', data),

  // Staff attendance / check-in
  getCheckinSummary: (month, year) => req('GET', `/teacher-checkins/summary?month=${month}&year=${year}`),
  markStaffLeave:    (data)        => req('POST', '/teacher-checkins/mark-leave', data),

  // First-login password change
  changePasswordFirst: (newPassword) => req('POST', '/auth/change-password-first', { newPassword }),

  // Auto-create parent/teacher accounts from existing student/staff records
  autoCreateAccounts: () => req('POST', '/auth/auto-create-accounts', {}),

  // Transport fee terms (DB-backed)
  getTransportTerms:  ()      => req('GET',  '/transport/fee-terms'),
  saveTransportTerms: (terms) => req('POST', '/transport/fee-terms/save', { terms }),

  // Generic POST (legacy usage in some pages)
  post: (path, data) => req('POST', path, data),

  // Textbooks / Question generator (local-only, no backend table)
  listTextbooks: () => {
    try { return Promise.resolve(JSON.parse(localStorage.getItem('sms_textbooks') || '[]')); }
    catch { return Promise.resolve([]); }
  },
  uploadTextbook: (_file, meta) => {
    const books = JSON.parse(localStorage.getItem('sms_textbooks') || '[]');
    const book = { id: `TB-${Date.now()}`, ...meta, chapters: [], uploadedAt: new Date().toISOString() };
    books.push(book);
    localStorage.setItem('sms_textbooks', JSON.stringify(books));
    return Promise.resolve(book);
  },
  updateTextbook: (id, data) => {
    const books = JSON.parse(localStorage.getItem('sms_textbooks') || '[]');
    const updated = books.map(b => b.id === id ? { ...b, ...data } : b);
    localStorage.setItem('sms_textbooks', JSON.stringify(updated));
    return Promise.resolve({ success: true });
  },
  deleteTextbook: (id) => {
    const books = JSON.parse(localStorage.getItem('sms_textbooks') || '[]');
    localStorage.setItem('sms_textbooks', JSON.stringify(books.filter(b => b.id !== id)));
    return Promise.resolve({ success: true });
  },
  generateQuestions: (params) => req('POST', '/question-papers/generate', params).catch(() => ({ questions: [] })),

  // Health / connectivity check (no auth needed)
  isOnline: async () => {
    try {
      const r = await fetch('/api/setup/status');
      const d = await r.json();
      return d?.configured === true;
    } catch { return false; }
  },
};
